/* eslint-disable @typescript-eslint/dot-notation */
import { NgFor, NgIf } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NbButtonModule, NbCardModule, NbDialogService, NbIconModule, NbRouteTabsetModule } from '@nebular/theme';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { OktaRestriction } from 'src/app/core/services/okta-restrictions.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { RouterState } from 'src/app/features/auth/redux/router-state';
import { Drb } from 'src/app/models/drb-models/drb';
import { SourceImage } from 'src/app/models/drb-models/source-image';
import { SourceItem } from 'src/app/models/drb-models/source-item';
import { Xliff } from 'src/app/models/drb-models/xliff';
import { ACTIONS, BUNDLE_NAMES, DRB_ID_TYPE, MODE } from 'src/app/shared/constants/general-constants';
import { NgxGenericModalComponent } from 'src/app/shared/ui/ngx-generic-modal/ngx-generic-modal.component';
import { SpinnerState } from 'src/app/shared/ui/spinner/redux/spinner.state';
import { ENVIRONMENT } from 'src/environments/environment';
import { SharedModule } from '../../../shared/shared.module';
import { DeleteDrbComponent } from '../delete-drb/delete-drb.component';
import { EditXliffComponent } from '../drb-xliff/edit-xliff/edit-xliff.component';
import { ViewXliffComponent } from '../drb-xliff/view-xliff/view-xliff.component';
import { NewDrbComponent } from '../new-drb/new-drb.component';
import { DrbState, LoadDrb, LoadDrbByVersion, LoadDrbVersions, SaveDrbState } from '../redux';
import { DrbService } from '../services/drb.service';
import { ManageContributorsComponent } from './manage-contributors/manage-contributors.component';
import { EditImageComponent } from './source-images/edit-image/edit-image.component';
import { ViewImageComponent } from './source-images/view-image/view-image.component';
import { EditSourceItemComponent } from './source-item/edit-source-item/edit-source-item.component';
import { ViewSourceItemComponent } from './source-item/view-source-item/view-source-item.component';

@Component({
  selector: 'app-drb-sources',
  templateUrl: './drb-sources.component.html',
  styleUrls: ['./drb-sources.component.css'],
  imports: [
    NgIf,
    SharedModule,
    NbCardModule,
    NgSelectComponent,
    FormsModule,
    NgFor,
    NgOptionComponent,
    NbButtonModule,
    NbIconModule,
    NbRouteTabsetModule
  ]
})
export class DrbSourcesComponent implements OnInit, OnDestroy {
  public marketing$: Observable<Drb> = inject(Store).select(DrbState.marketing);
  public marketing_translations$: Observable<Drb> = inject(Store).select(DrbState.marketing_translations);
  public version$: Observable<any> = inject(Store).select(DrbState.versions);
  public translating$: Observable<boolean> = inject(Store).select(DrbState.translating);
  public loading$: Observable<boolean> = inject(Store).select(SpinnerState.isLoading);
  public deploying$: Observable<boolean> = inject(Store).select(DrbState.deploying);
  public username$: Observable<string> = inject(Store).select(RouterState.username);
  private unsubscribe: Subject<void> = new Subject();

  private DEPLOYING_STATES = ['BUILD_STARTED', 'DEPLOY_STARTED_PROD', 'DEPLOY_STARTED_STAGE', 'BUILD_SUCCEEDED'];

  public spin;
  public mode = MODE.CREATE;
  public drb;
  public resourceData;
  public xliff;
  public sourceFiles;
  public sourceImages;
  public sourceLinks;
  public sourceVideos;
  public username;
  public versions: any[];
  public versionSelected;
  public draftVersion;
  public environment: string = ENVIRONMENT.name;
  public bundle_name: string;

  public strings;
  public files;
  public images;
  public links;
  public videos;
  public showActions = [];
  public translating;
  public deploying;

  constructor(
    private router: Router,
    private service: DrbService,
    private route: ActivatedRoute,
    private store: Store,
    private dialogService: NbDialogService,
    private restrictionService: OktaRestriction,
    public utility: UtilityService
  ) {}

  public ngOnInit() {
    this.version$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (this.bundle_name && data[this.bundle_name].length > 0) {
        this.versions = [...data[this.bundle_name]].reverse();
        this.draftVersion = this.versions[0];
        if (!this.draftVersion.endsWith('draft')) {
          this.drb = undefined;
          this.dialogService.open(NewDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              bundle_name: this.bundle_name
            }
          });
        }
        this.versionSelected = false;
        if (!this.store.selectSnapshot(DrbState.getByKey(this.bundle_name)).version) {
          this.store.dispatch(new LoadDrbByVersion(this.draftVersion, this.bundle_name));
        }
      }
    });
    this.translating$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.translating = data));
    this.deploying$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.deploying = data));
    this.loading$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.spin = data));
    this.username$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.username = data));
    this.marketing$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data.version && this.bundle_name === BUNDLE_NAMES.MARKETING) {
        this.deploying = this.DEPLOYING_STATES.includes(data.status);
        this.translating = data.translations_submitted && !data.translations_received;
        this.drb = data;
        this.strings = data.xliff.en;
        this.files = data.source.files;
        this.images = data.source.images;
        this.links = data.source.links;
        this.videos = data.source.videos;
        this.checkOwnerOrContributor();
      }
    });
    this.marketing_translations$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      if (data.version && this.bundle_name === BUNDLE_NAMES.MARKETING_TRANSLATIONS) {
        this.deploying = this.DEPLOYING_STATES.includes(data.status);
        this.translating = data.translations_submitted && !data.translations_received;
        this.drb = data;
        this.strings = data.xliff.en;
        this.files = data.source.files;
        this.images = data.source.images;
        this.links = data.source.links;
        this.videos = data.source.videos;
        this.checkOwnerOrContributor();
      }
    });
    this.route.url.subscribe((event) => {
      this.bundle_name = event[1].path;
      this.loadData();
    });
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public onChange(event) {
    return;
  }

  public manageContributors(): void {
    this.dialogService.open(ManageContributorsComponent, {
      closeOnEsc: false,
      closeOnBackdropClick: false,
      context: {
        contributors: JSON.parse(JSON.stringify(this.drb.contributors)),
        username: this.username,
        bundle_name: this.bundle_name
      }
    });
  }

  public checkOwnerOrContributor() {
    if (this.username === this.drb.owner || this.drb.contributors.includes(this.username)) {
      if (this.restrictionService.manager_permission) {
        this.showActions = [ACTIONS.CREATE, ACTIONS.EDIT];
        if (!ENVIRONMENT.isProduction) {
          this.showActions.push(ACTIONS.DELETE);
        }
      }
    } else {
      this.showActions = [];
    }
  }

  public reload() {
    this.store.dispatch(new LoadDrb(this.bundle_name));
  }

  public loadData() {
    if (this.store.selectSnapshot(DrbState.versions)[this.bundle_name].length === 0) {
      this.store.dispatch(new LoadDrbVersions(this.bundle_name));
    } else {
      let drb = this.store.selectSnapshot(DrbState.getByKey(this.bundle_name));
      this.versions = [...this.store.selectSnapshot(DrbState.versions)[this.bundle_name]].reverse();
      this.draftVersion = this.versions[0];
      if (!this.draftVersion.endsWith('draft')) {
        this.drb = undefined;
        this.dialogService.open(NewDrbComponent, {
          closeOnEsc: false,
          closeOnBackdropClick: false,
          context: {
            bundle_name: this.bundle_name
          }
        });
        return;
      }
      this.versionSelected = false;
      this.deploying = this.DEPLOYING_STATES.includes(drb.status);
      this.translating = drb.translations_submitted && !drb.translations_received;
      this.drb = drb;
      this.strings = drb.xliff.en;
      this.files = drb.source.files;
      this.images = drb.source.images;
      this.links = drb.source.links;
      this.videos = drb.source.videos;
      this.checkOwnerOrContributor();
    }
  }

  public displayContributors() {
    const contributors = JSON.parse(JSON.stringify(this.drb.contributors)).filter((element) => element.length > 0);

    return contributors.join(', ');
  }

  public optForTranslation() {
    const localDrb = JSON.parse(JSON.stringify(this.drb));

    localDrb.last_updated_by = this.username;
    localDrb.last_updated_ts = Math.floor(Date.now() / 1000);
    localDrb.translate = !this.drb.translate;
    localDrb.bundle_name = this.bundle_name;

    this.store.dispatch(new SaveDrbState(localDrb, this.bundle_name));
  }

  public getNextMonday(date = new Date()) {
    const dateCopy = new Date(date.getTime());
    let nextMonday;
    if (dateCopy.getHours() < 16) {
      nextMonday = new Date(dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + 1) % 7)));
    } else {
      nextMonday = new Date(dateCopy.setDate(dateCopy.getDate() + ((7 - dateCopy.getDay() + 1) % 7 || 7)));
    }

    return nextMonday.getFullYear() + '/' + (nextMonday.getMonth() + 1) + '/' + nextMonday.getDate();
  }

  public getTwoWeeksFromNow(timestamp: number) {
    const date = timestamp + 86400 * 14;

    return this.utility.convertUnixTime_global(date);
  }

  public clickedButton(type, $event) {
    if ($event.action === ACTIONS.REFRESH) {
      this.dialogService
        .open(NgxGenericModalComponent, {
          context: {
            title: $localize`Confirm Refresh`,
            message: $localize`Are you sure that you want to refresh draft version?`
          }
        })
        .onClose.subscribe((data) => {
          if (data) {
            this.strings = undefined;
            this.files = undefined;
            this.images = undefined;
            this.links = undefined;
            this.videos = undefined;
            this.store.dispatch(new LoadDrb(this.bundle_name));
          }
        });
    }
    if ($event.action === ACTIONS.CREATE) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          this.dialogService.open(EditXliffComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              xliff: new Xliff(),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: new SourceItem(),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          this.dialogService.open(EditImageComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              image: new SourceImage(),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: new SourceItem(),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: new SourceItem(),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
      }
    }
    if ($event.action === ACTIONS.VIEW) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          const xliff: Xliff = $event.data;
          this.dialogService.open(ViewXliffComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              xliff: xliff
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          const file: SourceItem = $event.data;
          this.dialogService.open(ViewSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              sourceItem: file,
              type: type
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          const image: SourceItem = $event.data;
          this.dialogService.open(ViewImageComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              image: JSON.parse(JSON.stringify(image))
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          const link: SourceItem = $event.data;
          this.dialogService.open(ViewSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              sourceItem: link,
              type: type
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          const video: SourceItem = $event.data;
          this.dialogService.open(ViewSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              sourceItem: video,
              type: type
            }
          });
          break;
      }
    }
    if ($event.action === ACTIONS.EDIT) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          const xliff: Xliff = $event.data;
          this.dialogService.open(EditXliffComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              xliff: JSON.parse(JSON.stringify(xliff)),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          const file: SourceItem = $event.data;
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: JSON.parse(JSON.stringify(file)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          const image: SourceImage = $event.data;
          this.dialogService.open(EditImageComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              image: JSON.parse(JSON.stringify(image)),
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          const link: SourceItem = $event.data;
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: JSON.parse(JSON.stringify(link)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          const video: SourceItem = $event.data;
          this.dialogService.open(EditSourceItemComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              action: $event.action,
              sourceItem: JSON.parse(JSON.stringify(video)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
      }
    }
    if ($event.action === ACTIONS.DELETE) {
      switch (type) {
        case DRB_ID_TYPE.STRING:
          const xliff: Xliff = $event.data;
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify(xliff)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.FILE:
          const file: SourceItem = $event.data;
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify(file)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.IMAGE:
          const image: SourceImage = $event.data;
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify(image)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.LINK:
          const link: SourceItem = $event.data;
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify(link)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
        case DRB_ID_TYPE.VIDEO:
          const video: SourceItem = $event.data;
          this.dialogService.open(DeleteDrbComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
            context: {
              item: JSON.parse(JSON.stringify(video)),
              type: type,
              bundle_name: this.bundle_name
            }
          });
          break;
      }
    }
  }
}
